import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { Input, Table, Form, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import Icon from '@mdi/react';
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiContentCopy,
  mdiPlusCircleOutline,
  mdiTrashCanOutline,
} from '@mdi/js';
import { useQuery } from '@tanstack/react-query';
import { useFlightBooking } from '../context/FlightBookingContext';
import { flightBookingHeaders } from './constants';
import { fetchTravelRequestEnquiryFilter } from '../../../store/apis/travelService';
import BookingAsyncAutocomplete from './BookingAsyncAutocomplete';
import { fetchPickListDetailsAutoComplete } from '../../../store/apis/autocompleteService';
import TravelAsyncAutocompleteSelect from '../TravelAsyncAutocompleteSelect/TravelAsyncAutocompleteSelect';
import * as icons from '../../../assets';
import { DatePicker, TimePicker } from '../../Common';
import './FlightOption.scss';

const FlightOption = forwardRef(
  (
    {
      index,
      control,
      trigger,
      getValues,
      setValue,
      flightOptions,
      headerDetails,
      status,
      flightBookingErrors,
      currentCompany,
    },
    ref,
  ) => {
    // Access the form control and validation methods
    const {
      activeOption,
      markOptionComplete,
      flightInfo,
      setActiveOption,
      optionStatus,
      goNext,
      goPrev,
      setOptionComplete,
    } = useFlightBooking();
    const { BaseCurr: baseCurr } = currentCompany || {};
    const formatSelectValue = (val, max = 20) => {
      if (!val) return null;

      // If val is already { value, label }
      if (typeof val === 'object' && val !== null) {
        const value = val.value ?? '';
        const rawLabel = val.label ?? ''; // don't fall back to the object
        const sLabel = String(rawLabel);
        const label =
          sLabel.length > max ? `${sLabel.slice(0, max)}...` : sLabel;
        return { value, label };
      }

      // If val is a primitive (string/number)
      const s = String(val);
      const label = s.length > max ? `${s.slice(0, max)}...` : s;
      return { value: val, label };
    };

    // Fetch Travel Agency Options
    const { data: travelAgencyOptions } = useQuery({
      queryKey: ['travelTypeOptions'],
      queryFn: () =>
        fetchTravelRequestEnquiryFilter({
          codeType: 'TRAVEL_AGENCY',
        }),
    });

    // Use the useFieldArray hook to manage the quotes array for this option
    const { fields, append, remove } = useFieldArray({
      control,
      name: `options.${index}.quotes`,
    });

    // Clean up quotes when the form loads or flight options change
    useEffect(() => {
      const options = getValues('options');

      const cleanedOptions = options.map(option => {
        const cleanedQuotes = option.quotes.map(quote => {
          const isEmptyString = val =>
            val === null || val === '' || val === '          ';

          const cleanField = val => (isEmptyString(val?.value) ? null : val);

          return {
            ...quote,
            fromCity: cleanField(quote.fromCity),
            fromCityName: isEmptyString(quote.fromCityName)
              ? ''
              : quote.fromCityName,
            toCountry: cleanField(quote.toCountry),
            toCountryName: isEmptyString(quote.toCountryName)
              ? ''
              : quote.toCountryName,
            toCity: cleanField(quote.toCity),
            date: quote.date === '1900-01-01' ? '' : quote.date,
            deptTime: quote.deptTime || '',
            airline: cleanField(quote.airline),
            airlineName: isEmptyString(quote.airlineName)
              ? ''
              : quote.airlineName,
            class: quote.class || '',
            traveller: quote.traveller || '',
          };
        });

        return {
          ...option,
          quotes: cleanedQuotes,
        };
      });

      // Update the cleaned quotes back into the form
      cleanedOptions.forEach((opt, i) => {
        setValue(`options.${i}.quotes`, opt.quotes, {
          shouldDirty: true,
          shouldValidate: false,
        });
      });
    }, [getValues, setValue, activeOption]);

    const hasAtLeastOneCompleteQuote = React.useCallback(
      values =>
        Array.isArray(values?.quotes) &&
        values.quotes.some(
          q =>
            q?.fromCity &&
            q?.toCountry &&
            q?.toCity &&
            q?.date &&
            q?.deptTime &&
            q?.airline &&
            q?.class,
        ),
      [],
    );

    const hasHeaderFields = React.useCallback(
      values =>
        !!values?.agency &&
        !!values?.currency &&
        !!values?.quoteValue &&
        Number(values?.quoteValue) > 0,
      [],
    );

    const isOptionCompleteNow = React.useCallback(
      optIndex => {
        const values = getValues(`options.${optIndex}`);
        return hasAtLeastOneCompleteQuote(values) && hasHeaderFields(values);
      },
      [getValues, hasAtLeastOneCompleteQuote, hasHeaderFields],
    );

    const didInitFromServerRef = useRef(false);

    // Autopopulate flight options when flightOptions prop changes
    useEffect(() => {
      if (flightOptions && flightOptions.segments?.length) {
        const mappedQuotes = flightOptions.segments.map(seg => {
          const travelDate = new Date(seg.TravelDate)
            .toISOString()
            .split('T')[0];

          return {
            fromCity: {
              value: seg.TravelFrom,
              label: seg.TravelFrom_Description || seg.TravelFrom,
            },
            fromCityName: seg.TravelFrom_Description || seg.TravelFrom,
            toCountry: {
              value: seg.TravelToCountry,
              label: seg.TravelToCountry_Description || seg.TravelToCountry,
            },
            toCountryName:
              seg.TravelToCountry_Description || seg.TravelToCountry,
            toCity: {
              value: seg.TravelTo,
              label: seg.TravelTo_Description || seg.TravelTo,
            },
            date: travelDate,
            deptTime: (seg.TravelTime ?? '').slice(0, 5),
            airline: {
              value: seg.Airline,
              label: seg.Airline_Description || seg.Airline,
            },
            airlineName: seg.Airline_Description || seg.Airline,
            class: seg.TravelClass,
            traveller: seg.TravelClient,
          };
        });

        // Set quotes array
        setValue(`options.${index}.quotes`, mappedQuotes, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });

        // Set additional fields (if available in headerDetails)
        const optNo = index + 1;
        setValue(
          `options.${index}.currency`,
          headerDetails?.[`Option${optNo}_Flight_Curr`]?.trim() || '',
        );
        setValue(
          `options.${index}.agency`,
          headerDetails?.[`Option${optNo}_Flight_TravelAgency`]?.trim() || '',
        );
        setValue(
          `options.${index}.quoteValue`,
          headerDetails?.[`Option${optNo}_Flight_QuoteValue`] === undefined ||
            headerDetails?.[`Option${optNo}_Flight_QuoteValue`] === null ||
            headerDetails?.[`Option${optNo}_Flight_QuoteValue`] === '' ||
            headerDetails?.[`Option${optNo}_Flight_QuoteValue`] === '0'
            ? ''
            : parseFloat(
                headerDetails?.[`Option${optNo}_Flight_QuoteValue`],
              ).toFixed(2),
        );
        setValue(
          `options.${index}.refund`,
          headerDetails?.[`Option${optNo}_Flight_Refund`] === undefined ||
            headerDetails?.[`Option${optNo}_Flight_Refund`] === null ||
            headerDetails?.[`Option${optNo}_Flight_Refund`] === '' ||
            headerDetails?.[`Option${optNo}_Flight_Refund`] === '0'
            ? ''
            : parseFloat(
                headerDetails?.[`Option${optNo}_Flight_Refund`],
              ).toFixed(2),
        );
        setValue(
          `options.${index}.rebooking`,
          headerDetails?.[`Option${optNo}_Flight_Rebooking`] || '',
        );
        setValue(
          `options.${index}.terms`,
          headerDetails?.[`Option${optNo}_Flight_Terms`] || '',
        );

        Promise.resolve().then(() => {
          if (isOptionCompleteNow(index)) {
            markOptionComplete(index);
          } else {
            setOptionComplete(index, false);
          }
          didInitFromServerRef.current = true;
        });
      } else {
        setValue(`options.${index}.currency`, baseCurr || '');
      }

      if (!didInitFromServerRef.current) {
        Promise.resolve().then(() => {
          if (isOptionCompleteNow(index)) {
            markOptionComplete(index);
          }
          didInitFromServerRef.current = true;
        });
      }
    }, [
      flightOptions,
      setValue,
      index,
      headerDetails,
      baseCurr,
      didInitFromServerRef,
      isOptionCompleteNow,
      markOptionComplete,
      setOptionComplete,
    ]);

    // Clear a specific row
    const clearRow = rowIndex => {
      const basePath = `options.${index}.quotes.${rowIndex}`;

      if (fields.length === 1) {
        // Just clear the values if it's the only row
        const emptyRow = {
          fromCity: null,
          fromCityName: '',
          toCountry: null,
          toCountryName: null,
          toCity: null,
          date: '',
          deptTime: '',
          airline: null,
          airlineName: '',
          class: '',
          traveller: '',
        };

        Object.entries(emptyRow).forEach(([fieldKey, emptyValue]) => {
          setValue(`${basePath}.${fieldKey}`, emptyValue);
        });
      } else {
        // Remove the row if there are multiple rows
        remove(rowIndex);
      }

      trigger(); // Refresh validation/UI
    };

    // Render Flight Option Rows
    const renderFlightOptionRows = () =>
      fields.map((item, i) => (
        <tr key={item.id}>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.fromCity`}
              control={control}
              render={({ field, fieldState }) => (
                <BookingAsyncAutocomplete
                  {...field}
                  error={!!fieldState.error}
                  autoCompleteFn={input =>
                    fetchPickListDetailsAutoComplete({
                      prefixText: input,
                      type: 'TRAVEL_CITY',
                      param1: '',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(fromCity => ({
                      value: fromCity.Code,
                      label: fromCity.Description,
                    }))
                  }
                  value={formatSelectValue(field.value, 20)}
                  onChange={option => {
                    setValue(`options.${index}.quotes.${i}.fromCity`, option, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue(
                      `options.${index}.quotes.${i}.fromCityName`,
                      option?.label || '',
                      {
                        shouldValidate: false,
                        shouldDirty: true,
                      },
                    );
                  }}
                  placeholder="From City"
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  width="160px"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                  }}
                />
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.fromCity && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.fromCity
                    .message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.toCountry`}
              control={control}
              render={({ field, fieldState }) => (
                <BookingAsyncAutocomplete
                  {...field}
                  error={!!fieldState.error}
                  autoCompleteFn={input =>
                    fetchPickListDetailsAutoComplete({
                      prefixText: input,
                      type: 'TRAVEL_COUNTRY',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(toCountry => ({
                      value: toCountry.Code,
                      label: toCountry.Description,
                    }))
                  }
                  value={formatSelectValue(field.value, 20)}
                  onChange={option => {
                    setValue(`options.${index}.quotes.${i}.toCountry`, option, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue(
                      `options.${index}.quotes.${i}.toCountryName`,
                      option?.label || '',
                      {
                        shouldValidate: true,
                        shouldDirty: true,
                      },
                    );
                  }}
                  placeholder="To Country"
                  isClearable
                  displayOption="bothCustom"
                  inputWidth="100%"
                  menuPortalTarget={document.body}
                  width="180px"
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                  }}
                />
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.toCountry && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.toCountry
                    ?.message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.toCity`}
              control={control}
              render={({ field, fieldState }) => (
                <BookingAsyncAutocomplete
                  {...field}
                  error={!!fieldState.error}
                  autoCompleteFn={input => {
                    const toCountry = getValues(
                      `options.${index}.quotes.${i}.toCountry`,
                    );
                    return fetchPickListDetailsAutoComplete({
                      prefixText: input,
                      type: 'TRAVEL_CITY',
                      param1: toCountry?.value ?? '',
                    });
                  }}
                  mappedFn={rawData =>
                    rawData.map(toCity => ({
                      value: toCity.Code,
                      label: toCity.Description,
                    }))
                  }
                  value={formatSelectValue(field.value, 20)}
                  onChange={option => {
                    setValue(`options.${index}.quotes.${i}.toCity`, option, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue(
                      `options.${index}.quotes.${i}.toCityName`,
                      option?.label || '',
                      {
                        shouldValidate: false,
                        shouldDirty: true,
                      },
                    );
                  }}
                  placeholder="To City"
                  isClearable
                  displayOption="bothCustom"
                  width="160px"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                  }}
                />
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.toCity && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.toCity
                    .message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.date`}
              control={control}
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  disabled={index > activeOption}
                  borderStyle="none"
                  className={`pe-0 ${fieldState.error ? 'is-invalid' : ''}`}
                  calendarFocus={false}
                />
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.date && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.date
                    ?.message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.deptTime`}
              control={control}
              render={({ field, fieldState }) => (
                <TimePicker
                  {...field}
                  disabled={index > activeOption}
                  interval={1}
                  className={`border-0 time-picker-pointer external-filter-input ${fieldState.error ? 'is-invalid' : ''}`}
                  onChange={val => field.onChange(val)}
                />
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.deptTime && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.deptTime
                    ?.message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.airline`}
              control={control}
              render={({ field, fieldState }) => (
                <BookingAsyncAutocomplete
                  {...field}
                  error={!!fieldState.error}
                  autoCompleteFn={input =>
                    fetchPickListDetailsAutoComplete({
                      prefixText: input,
                      type: 'TRAVEL_AIRLINE',
                    })
                  }
                  mappedFn={rawData =>
                    rawData.map(airline => ({
                      value: airline.Code,
                      label: airline.Description,
                    }))
                  }
                  value={formatSelectValue(field.value, 20)}
                  onChange={option => {
                    setValue(`options.${index}.quotes.${i}.airline`, option, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setValue(
                      `options.${index}.quotes.${i}.airlineName`,
                      option?.label || '',
                      {
                        shouldValidate: false,
                        shouldDirty: true,
                      },
                    );
                  }}
                  width="160px"
                  placeholder="Airline"
                  isClearable
                  displayOption="bothCustom"
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 2000 }),
                  }}
                />
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.airline && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.airline
                    ?.message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.class`}
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  type="select"
                  className={`form-select ps-2 border-0 ${fieldState.error ? 'is-invalid' : ''}`}
                  {...field}
                  disabled={index > activeOption}
                  placeholder="Class"
                  onChange={val => field.onChange(val)}
                >
                  <option value="">Select Class</option>
                  <option value="Economy">Economy</option>
                  <option value="Business">Business</option>
                </Input>
              )}
            />
            {flightBookingErrors?.options?.[index]?.quotes?.[0]?.class && (
              <div className="text-danger-tr ps-2 pb-2">
                {
                  flightBookingErrors?.options?.[index]?.quotes?.[0]?.class
                    ?.message
                }
              </div>
            )}
          </td>
          <td className="p-0">
            <Controller
              name={`options.${index}.quotes.${i}.traveller`}
              control={control}
              render={({ field }) => (
                <Input
                  className=" border-0"
                  type="text"
                  {...field}
                  disabled={index > activeOption}
                  placeholder="Traveller"
                  onChange={val => field.onChange(val)}
                />
              )}
            />
          </td>
          <td className="p-0 text-center">
            <button
              type="button"
              className="btn btn-link py-1"
              onClick={() => clearRow(i)}
              title="Delete Row"
            >
              <span className="visually-hidden">Delete Row</span>
              <Icon path={mdiTrashCanOutline} size={0.7} color="#e71d36" />
            </button>
          </td>
        </tr>
      ));

    // Function to Add New Row if Last Row is Valid
    const addRowIfValid = () => {
      const last = getValues(`options.${index}.quotes.${fields.length - 1}`);

      const isFilled = Object.entries(last).every(([key, value]) => {
        if (key === 'traveller') return true; // skip check for traveller
        return value !== undefined && value !== null && value !== '';
      });

      if (isFilled) {
        append({
          fromCity: null,
          toCountry: null,
          toCity: null,
          date: null,
          deptTime: null,
          airline: null,
          class: null,
          traveller: '',
        });
      } else {
        toast.error(
          'Please fill all fields in the current row before adding a new one.',
          {
            className: 'toast-error',
          },
        );
      }
    };

    // Apply flight information from the context
    const applyFlightInfo = () => {
      if (!flightInfo || !Array.isArray(flightInfo)) {
        toast.error('No flight information to copy.', {
          className: 'toast-error',
        });
        return;
      }

      // Helper: normalize blank/empty values to null
      const normalize = val => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string' && val.trim() === '') return '';
        return val;
      };

      const quotes = flightInfo.map(item => {
        const isoDate = new Date(item.TravelDate).toISOString().split('T')[0];

        return {
          fromCity: normalize(item.TravelFrom)
            ? {
                value: item.TravelFrom.trim(),
                label:
                  normalize(item.TravelFrom_Description) ||
                  item.TravelFrom.trim(),
              }
            : null,
          fromCityName:
            normalize(item.TravelFrom_Description) ||
            normalize(item.TravelFrom),
          toCountry: normalize(item.TravelToCountry)
            ? {
                value: item.TravelToCountry.trim(),
                label:
                  normalize(item.TravelToCountry_Description) ||
                  item.TravelToCountry.trim(),
              }
            : null,
          toCountryName:
            normalize(item.TravelToCountry_Description) ||
            normalize(item.TravelToCountry),
          toCity: normalize(item.TravelTo)
            ? {
                value: item.TravelTo.trim(),
                label:
                  normalize(item.TravelTo_Description) || item.TravelTo.trim(),
              }
            : null,
          date: normalize(item.TravelDate) ? isoDate : null,
          deptTime: normalize(item.TravelTime),
          airline: normalize(item.Airline)
            ? {
                value: item.Airline.trim(),
                label:
                  normalize(item.Airline_Description) || item.Airline.trim(),
              }
            : null,
          airlineName:
            normalize(item.Airline_Description) || normalize(item.Airline),
          class: normalize(item.TravelClass),
          traveller: normalize(item.TravelClient),
        };
      });

      // Replace all rows
      remove();
      quotes.forEach(q => append(q));
    };

    // Access the form methods from react-hook-form
    useImperativeHandle(ref, () => ({
      validate: async () => {
        const valid = await trigger(); // basic field validation
        const values = getValues(`options.${index}`); // get this option's full object

        const hasAtLeastOneQuote = values.quotes?.some(
          quote =>
            quote.fromCity &&
            quote.toCountry &&
            quote.toCity &&
            quote.date &&
            quote.deptTime &&
            quote.airline &&
            quote.class &&
            quote.traveller,
        );

        const hasRequiredFields =
          values.agency && values.currency && values.quoteValue;

        if (valid && hasAtLeastOneQuote && hasRequiredFields) {
          markOptionComplete(index);
          return true;
        }

        toast.error(
          'Please fill all required fields and at least one row of flight booking quotation.',
          {
            className: 'toast-error',
          },
        );
        return false;
      },
    }));

    // Check if the current index matches the active option
    const isRebookingMode =
      status === 'Request for Rebooking' ||
      status === 'Pending for Booking' ||
      status === 'Request for Cancellation';

    useEffect(() => {
      if (isRebookingMode && index !== activeOption) {
        setActiveOption(index);
      }
    });

    if (!isRebookingMode && index !== activeOption) return null;

    const handleNext = async () => {
      const valid = await trigger(`options.${activeOption}`);
      const values = getValues(`options.${activeOption}`);

      const hasAtLeastOneQuote = values.quotes?.some(
        quote =>
          quote.fromCity &&
          quote.toCountry &&
          quote.toCity &&
          quote.date &&
          quote.deptTime &&
          quote.airline &&
          quote.class,
      );

      const hasRequiredFields =
        values.agency && values.currency && values.quoteValue;

      if (valid && hasAtLeastOneQuote && hasRequiredFields) {
        markOptionComplete(index);
        goNext();
        return true;
      }

      toast.error(
        'Please fill all required fields and at least one row of flight booking quotation.',
        {
          className: 'toast-error',
        },
      );
      return false;
    };

    const handlePrev = () => {
      goPrev();
    };

    // Turn options into a clean array regardless of its shape
    function normalizeOptions(options) {
      if (Array.isArray(options)) return options;
      if (options && typeof options === 'object') {
        return Object.keys(options)
          .filter(k => String(+k) === k) // numeric keys only
          .sort((a, b) => +a - +b)
          .map(k => options[k]);
      }
      return [];
    }

    // Deep-emptiness check: true means "no real error content inside"
    function isEmptyDeep(val) {
      if (val == null) return true; // null/undefined
      if (Array.isArray(val)) return val.every(isEmptyDeep);
      if (typeof val === 'object') {
        const vals = Object.values(val);
        if (vals.length === 0) return true; // {}
        return vals.every(isEmptyDeep);
      }
      return false;
    }

    function buildOptionErrorMessage(errors = {}) {
      const optionNumbers = new Set();

      const opts = normalizeOptions(errors.options);
      opts.forEach((opt, idx) => {
        if (!isEmptyDeep(opt)) {
          optionNumbers.add(idx + 1); // 1-based label
        }
      });

      if (optionNumbers.size === 0) return '';

      const labels = [...optionNumbers]
        .sort((a, b) => a - b)
        .map(n => `Option ${n}`);

      if (labels.length === 1) {
        return `Validation errors found in ${labels[0]}.`;
      }
      if (labels.length === 2) {
        return `Validation errors found in ${labels[0]} and ${labels[1]}.`;
      }
      const last = labels.pop();
      return `Validation errors found in ${labels.join(', ')} and ${last}.`;
    }

    const getOptionClass = option => {
      if (activeOption === option) {
        return 'active';
      }

      if (optionStatus[option]) {
        return 'completed';
      }

      return 'invalid';
    };

    const getOptionCompleted = option => {
      if (optionStatus[option]) {
        return icons.iconsBaChecked;
      }
      return icons.iconsBaUnchecked;
    };

    return (
      <div
        className={`${flightBookingErrors?.options?.root?.message ? 'is-invalid' : ''} ${flightBookingErrors?.options?.length > 0 ? 'is-invalid' : ''}`}
      >
        <div className="quote-header pb-3">
          <div className="fw-semibold h5">Flight Booking Options</div>
          {/* Options */}
          {!isRebookingMode && (
            <div className="quote-option-nav">
              <button
                type="button"
                onClick={handlePrev}
                disabled={activeOption === 0}
              >
                <Icon path={mdiChevronLeft} size={0.7} />
                <span>Previous</span>
              </button>
              <div className="quote-option-check">
                <div
                  className={`quote-option-check-item-0 ${getOptionClass(0)} ${activeOption === 0 ? 'option-check-active' : ''}`}
                >
                  <img
                    src={getOptionCompleted(0)}
                    alt="Checked Icon"
                    className="option-icon"
                  />
                  <span>Option 1</span>
                </div>
                <div
                  className={`quote-option-check-item-1 ${getOptionClass(1)} ${activeOption === 1 ? 'option-check-active' : ''}`}
                >
                  <img
                    src={getOptionCompleted(1)}
                    alt="Checked Icon"
                    className="option-icon"
                  />
                  <span>Option 2</span>
                </div>
                <div
                  className={`quote-option-check-item-2 ${getOptionClass(2)} ${activeOption === 2 ? 'option-check-active' : ''}`}
                >
                  <img
                    src={getOptionCompleted(2)}
                    alt="Checked Icon"
                    className="option-icon"
                  />
                  <span>Option 3</span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleNext}
                disabled={activeOption === 2}
              >
                <span>Next</span>
                <Icon path={mdiChevronRight} size={0.7} />
              </button>
            </div>
          )}

          {/* Action Buttons */}
          {!isRebookingMode && (
            <div className="d-flex gap-2 justify-content-end mb-2">
              <button
                type="button"
                className="ba-action-buttons"
                onClick={addRowIfValid}
                disabled={index > activeOption}
              >
                <Icon path={mdiPlusCircleOutline} size={0.8} color="#026AA2" />
                <span>Add Row</span>
              </button>
              <button
                type="button"
                className="ba-action-buttons"
                onClick={applyFlightInfo}
              >
                <Icon path={mdiContentCopy} size={0.8} color="#026AA2" />
                <span>Copy</span>
              </button>
            </div>
          )}
        </div>
        <Form>
          <div className="table-responsive mb-3 ba-flight-option-table-container">
            <Table className="ba-option-table" id="ba-flight-option-table">
              <thead>
                <tr>
                  {flightBookingHeaders.map((header, i) => (
                    <th key={i}>
                      <span>{header.columnName}</span>
                      {header.required && (
                        <span className="text-danger-tr">*</span>
                      )}
                    </th>
                  ))}
                  <th className="text-center">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>{renderFlightOptionRows()}</tbody>
            </Table>
          </div>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-row align-items-center gap-2">
              <Col className="external-filter-col">
                <span className="ba-form-label">
                  Travel Agency<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  control={control}
                  name={`options.${index}.agency`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      className="external-filter-input"
                      onChange={val => field.onChange(val)}
                    >
                      <option value="">Select Travel Agency</option>
                      {travelAgencyOptions?.map(option => (
                        <option key={option.Code} value={option.Code}>
                          {option.Description}
                        </option>
                      ))}
                    </Input>
                  )}
                />
                {flightBookingErrors?.options?.[index]?.agency && (
                  <span className="text-danger-tr">
                    {flightBookingErrors.options[index].agency.message}
                  </span>
                )}
              </Col>
              <Col className="external-filter-col">
                <span className="ba-form-label">
                  Currency<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  name={`options.${index}.currency`}
                  control={control}
                  render={({ field }) => (
                    <TravelAsyncAutocompleteSelect
                      {...field}
                      placeholder="Select Currency"
                      autoCompleteFn={input =>
                        fetchPickListDetailsAutoComplete({
                          prefixText: input,
                          type: 'HR_CURRENCY',
                        })
                      }
                      mappedFn={rawData =>
                        rawData.map(currency => ({
                          value: currency.Code,
                          label: currency.Name,
                        }))
                      }
                      onChange={option =>
                        setValue(`options.${index}.currency`, option.value, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }
                      value={field.value}
                    />
                  )}
                />
                {flightBookingErrors?.options?.[index]?.currency && (
                  <span className="text-danger-tr">
                    {flightBookingErrors.options[index].currency.message}
                  </span>
                )}
              </Col>
              <Col className="external-filter-col">
                <span className="ba-form-label">
                  Quote Value<span className="text-danger-tr">*</span>
                </span>
                <Controller
                  control={control}
                  name={`options.${index}.quoteValue`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      placeholder="0"
                      className="external-filter-input"
                      onChange={val => field.onChange(val)}
                      onBlur={e => {
                        const val = e.target.value;
                        field.onChange(
                          val === '' ? '' : parseFloat(val).toFixed(2),
                        );
                      }}
                    />
                  )}
                />
                {flightBookingErrors?.options?.[index]?.quoteValue && (
                  <span className="text-danger-tr">
                    {flightBookingErrors.options[index].quoteValue.message}
                  </span>
                )}
              </Col>
            </div>
            <div className="d-flex flex-row align-items-center gap-2">
              <Col className="external-filter-col">
                <span className="ba-form-label">Refund</span>
                <Controller
                  control={control}
                  name={`options.${index}.refund`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="number"
                      className="external-filter-input"
                      placeholder="0"
                      onChange={val => field.onChange(val)}
                      onBlur={e => {
                        const val = e.target.value;
                        field.onChange(
                          val === '' ? '' : parseFloat(val).toFixed(2),
                        );
                      }}
                    />
                  )}
                />
              </Col>
              <Col className="external-filter-col">
                <span className="ba-form-label">Re-booking</span>
                <Controller
                  control={control}
                  name={`options.${index}.rebooking`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="select"
                      placeholder="Re-booking"
                      className="external-filter-input"
                      onChange={val => field.onChange(val)}
                    >
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                    </Input>
                  )}
                />
              </Col>
              <Col />
            </div>
            <div>
              <Col md={12} className="external-filter-col">
                <span className="ba-form-label">Terms</span>
                <Controller
                  control={control}
                  name={`options.${index}.terms`}
                  render={({ field }) => (
                    <Input
                      {...field}
                      type="textarea"
                      className="external-filter-input"
                      placeholder="Terms"
                      onChange={val => field.onChange(val)}
                    />
                  )}
                />
              </Col>
            </div>
          </div>
          <div className="text-danger-tr pt-3">
            {buildOptionErrorMessage(flightBookingErrors)}
          </div>

          {flightBookingErrors?.options?.root?.message && (
            <div className="text-danger-tr pt-3">
              {flightBookingErrors?.options?.root?.message}
            </div>
          )}
        </Form>
      </div>
    );
  },
);

export default FlightOption;
